import React from 'react';
import { Link } from 'react-router-dom';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';

function OverviewCards({ data, monthly }) {
    const smsInbox = useEyeconxStore(state => state.smsInbox);

    // Enhanced card data configuration
    const cards = [
        {
            title: "Total Messages",
            icon: "bi-envelope-paper",
            value: data.totalMessages || 0,
            iconColor: "text-primary", // Using Bootstrap's primary (blue)
            bgColor: "bg-primary bg-opacity-20", // Bootstrap's opacity utility
            iconBg: "bg-primary",
            breakdown: [
                { label: "Sent", value: data.messagesSent || 0, color: "primary" },
                { label: "Received", value: data.messagesReceived || 0, color: "success" }
            ]
        },
        {
            title: "Message Delivery",
            icon: "bi-send-check",
            value: data.messagesDelivered || 0,
            metric: `${data.sentSuccessRate || '0%'} success rate`,
            iconColor: "text-info", // Using Bootstrap's info (cyan/teal)
            metricColor: "text-info",
            bgColor: "bg-info bg-opacity-20",
            iconBg: "bg-info",
            breakdown: [
                { label: "In Process", value: data.messagesInProcess || 0, color: "warning" },
                { label: "Failed", value: data.messagesFailed || 0, color: "danger" }
            ]
        },
        {
            title: "Received Messages",
            icon: "bi-inbox",
            value: data.messagesReceived || 0,
            metric: `${data.messagesUnread || 0} Unread`,
            metricLink: "View inbox",
            iconColor: "text-success", // Using Bootstrap's success (green)
            metricColor: data.messagesUnread > 0 ? "text-danger" : "text-success",
            bgColor: "bg-success bg-opacity-20",
            iconBg: "bg-success",
            hasBadge: true,
            breakdown: [
                { label: "Read", value: data.readMessages || 0, color: "info" },
                { label: "Unread", value: data.messagesUnread || 0, color: "info" }
            ]
        },
        {
            title: "Patient Engagement",
            icon: "bi-people",
            value: data.uniquePatientsContacted || 0,
            metric: `${data.readRate || '0%'} read rate`,
            iconColor: "text-warning", // Using Bootstrap's warning (yellow)
            metricColor: "text-warning",
            bgColor: "bg-warning bg-opacity-20",
            iconBg: "bg-warning",
            breakdown: [
                { label: "Avg Msg Length", value: `${data.avgMessageLength || 0} chars`, color: "dark" }
            ]
        },
        {
            title: "Confirmation Required",
            icon: "bi-check-circle",
            value: data.confirmationRequiredMessages || 0,
            metric: `${Math.round((data.confirmationRequiredMessages / data.messagesSent) * 100) || 0}% of sent`,
            iconColor: "text-danger",
            metricColor: "text-danger",
            bgColor: "bg-danger bg-opacity-20",
            iconBg: "bg-danger"
        },
        {
            title: "Message Health",
            icon: "bi-heart-pulse",
            value: `${data.sentSuccessRate || '0%'}`,
            metric: `${data.sentFailureRate || '0%'} failure rate`,
            iconColor: "text-warning",
            metricColor: "text-warning",
            bgColor: "bg-warning bg-opacity-20", // Changed from "bg-indigo bg-opacity-20"
            iconBg: "bg-warning", // Changed from "bg-indigo"
            breakdown: [
                { label: "Delivered", value: data.messagesDelivered || 0, color: "success" },
                { label: "Failed", value: data.messagesFailed || 0, color: "danger" }
            ]
        },
        {
            title: "Read Rate",
            icon: "bi-eye",
            value: data.readRate || '0%',
            metric: `${data.readMessages || 0} of ${data.messagesReceived || 0} read`,
            iconColor: "text-info",
            metricColor: "text-info",
            bgColor: "bg-info bg-opacity-20",
            iconBg: "bg-info"
        },
        {
            title: "Message Length",
            icon: "bi-text-paragraph",
            value: `${data.avgMessageLength || 0} chars`,
            metric: `${Math.round(data.avgMessageLength / 160 * 100) || 0}% of limit`,
            iconColor: "text-danger",
            metricColor: "text-danger",
            bgColor: "bg-danger bg-opacity-20",
            iconBg: "bg-danger"
        }
    ];

    return (
        <div className="row g-3">
            {cards.map((card, index) => (
                <div key={index} className="col-md-6 col-lg-3">
                    <div className={`card border-0 shadow-sm h-100 ${card.bgColor}`}>
                        <div className="card-header bg-transparent border-0 pb-0">
                            <div className="d-flex align-items-center">
                                <span className={`icon-shape rounded-1 ${card.iconBg} text-white me-3 p-4`}>
                                    <i className={`bi ${card.icon} fs-4 text-white`}></i>
                                </span>
                                <h5 className="mb-0">{card.title}</h5>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <h1 className="display-5 fw-bold text-dark mb-2">
                                {card.value}
                            </h1>

                            {card.metric && !card.hasBadge && (
                                <div className={`mt-2 ${card.metricColor} small`}>
                                    <i className={`bi ${card.metricColor.includes('danger') ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-1`}></i>
                                    {card.metric}
                                </div>
                            )}

                            {card.hasBadge && (
                                <div className="d-flex align-items-center justify-content-between mt-3">
                                    <span className={`badge ${card.metricColor.includes('danger') ? 'bg-danger bg-opacity-20' : 'bg-success bg-opacity-20'} ${card.metricColor} me-2`}>
                                        <i className="bi bi-envelope me-1"></i>
                                        {card.metric}
                                    </span>
                                    <Link to="/sms-messages" className="text-primary small text-decoration-underline">
                                        {card.metricLink} <i className="bi bi-arrow-right"></i>
                                    </Link>
                                </div>
                            )}

                            {card.breakdown && (
                                <div className="mt-3">
                                    {card.breakdown.map((item, i) => (
                                        <div key={i} className="d-flex align-items-center mb-1">
                                            <div className={`bullet bg-${item.color} me-2`} style={{ width: '8px', height: '8px', borderRadius: '50%' }}></div>
                                            <small className="text-muted">{item.label}: <span className={`text-${item.color} fw-bold`}>{item.value}</span></small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default OverviewCards;